<template>
  <form class="car-subscription-form" @submit.prevent="onSubmit">
    <transition
      enter-active-class="duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <BaseErrorAlert v-if="alertVisible">
        Debe seleccionar el lugar de recogida.
      </BaseErrorAlert>
    </transition>
    <fieldset class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <template v-if="!hideVehicleCatalogFilters">
        <BaseLocationsAutocomplete
          v-if="pickedCar.stations.length < 2"
          :model-value="pickedCar.stations[0]"
          :label="$t('pick_up_location')"
          disabled
          class="col-span-full"
        />
        <BaseLocationsAutocomplete
          v-else
          v-model="cartStore.selectedBooking.station"
          :label="$t('pick_up_location')"
          class="col-span-full"
        />
      </template>
      <BaseDatePicker
        id="subscription-from"
        v-model="cartStore.selectedBooking.start"
        :min-date="new Date()"
        :label="$t('from')"
      />
      <BaseMonthsPicker
        v-model="cartStore.selectedBooking.monthsLong"
        :months="computedMonths"
        :label="$t('duration')"
      >
        <template #item="{ value, text }">
          {{ text }} ({{ getPriceByMonth(value) }})
        </template>
      </BaseMonthsPicker>

      <BaseKilometersPicker
        v-model="cartStore.selectedBooking.km"
        class="col-span-full"
        :label="`${$t('kilometers')} ${$t('included', 2)}`"
        :options="computedKms"
      >
        <template #item="{ value, text }">
          <div class="flex gap-2 items-center">
            <span>{{ text }}</span>
            <span>+{{ getPriceByKms(value) }}</span>
          </div>
        </template>
      </BaseKilometersPicker>
    </fieldset>

    <div
      :class="submitBtnClass"
    >
      <BaseButton
        size="xl"
        type="submit"
        class="mt-4 w-full"
        :loading="isLoadingFeatures"
      >
        {{ $t('continue') }}
      </BaseButton>
    </div>

    <SubscriptionLeadModal
      ref="$leadModal"
      @close="onModalClose"
    />
  </form>
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref, watch, computed } from 'vue';
import BaseButton from '~/components/BaseButton.vue';
import BaseLocationsAutocomplete from './BaseLocationsAutocomplete.vue';
import type { CarSpecs, KmPrice } from '~/types';
import { useCartStore } from '~/stores/cartStore';
import BaseDatePicker from '~/components/forms/BaseDatePicker.vue';
import BaseMonthsPicker from '~/components/forms/BaseMonthsPicker.vue';
import BaseKilometersPicker from '~/components/forms/BaseKilometersPicker.vue';
import BaseErrorAlert from '~/components/BaseErrorAlert.vue';
import { formatCurrency } from '~/lib/utils';
import { useFeatureHandler, useGTM } from '~/composables';
import { TRACKING_EVENTS } from '~/lib/constants';

const { hideVehicleCatalogFilters } = useFeatureHandler();
const SubscriptionLeadModal = defineAsyncComponent(() => import('~/components/modals/SubscriptionLeadModal.vue'));
const cartStore = useCartStore();
const $leadModal = ref(null);
const isLoadingFeatures = ref(false);
const alertVisible = ref(false);

interface Props {
  pickedCar: CarSpecs
  submitBtnClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  submitBtnClass: undefined,
});
cartStore.selectedBooking.station = props.pickedCar?.stations?.[0] || null;

const computedMonths = computed(() => props.pickedCar?.prices
  ?.map((price) => String(price.months))
  ?.toSorted((a, b) => Number(b) - Number(a)) || []);
const computedKms = computed(() => props.pickedCar?.kmPrices.map(item => String(item.km)));

// show modal
function showLeadModal () {
  const isLead = cartStore.selectedCar?.meta?.isLead;
  if (isLead) {
    ($leadModal.value as any)?.openModal();
  }
}

// eslint-disable-next-line no-undef
const router = useRouter();
async function onModalClose ({ isLeadSent }: { isLeadSent: boolean}) {
  if (isLeadSent) {
    // flush data
    await router.push('/subscription/catalog');
    cartStore.$reset();
  }
}
const { triggerEvent } = useGTM();
async function onSubmit () {
  alertVisible.value = false;
  triggerEvent(TRACKING_EVENTS.CLICK_CONTINUE_FORM_SUBSCRIPTION, cartStore.selectedBooking);
  if (!cartStore.selectedBooking.station?.id || !cartStore.selectedBooking.start || !cartStore.selectedBooking.monthsLong) {
    alertVisible.value = true;
    return;
  }
  cartStore.setSelectedCar(props.pickedCar);

  try {
    isLoadingFeatures.value = true;
    // TODO: load features once BE is ready
    return showLeadModal();
    // await cartStore.loadFeatures();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('[features list error]: ', error);
  } finally {
    isLoadingFeatures.value = false;
  }

  const hasFeatures = cartStore.features.length > 0;

  if (hasFeatures) {
    await router.push('/subscription/customization');
  } else {
    showLeadModal();
  }
}

function getPriceByMonth (month: number) {
  const match = props.pickedCar.prices.find(item => Number(item.months) === Number(month));
  const price = match?.price || null;

  return formatCurrency(price?.value || 0, price?.currency_name);
}

function getPriceByKms (km: string) {
  const match: KmPrice | undefined = props.pickedCar.kmPrices.find(item => Number(item.km) === Number(km));
  const price: KmPrice['price'] | null = match?.price || null;

  return formatCurrency(price?.value || 0, price?.currency_name);
}

watch(() => cartStore.selectedBooking, (newVal) => {
  if (newVal) {
    alertVisible.value = false;
  }
}, { deep: true, immediate: true });

defineExpose({ onSubmit, isLoadingFeatures });
</script>
